import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { sponsorsEnabled } from '@/lib/feature-flags';
import { roleToPath } from '@/lib/role-redirect';
import { useBearer } from '@/pages/admin/_admin-utils';

const navLinkClass = ({ isActive }: { isActive: boolean }): string =>
  `badge shadow-soft hover:bg-white/70 transition ${isActive ? 'bg-white text-cocoa' : 'bg-shell/70 text-cocoa/80'}`;

const mobileNavLinkClass = ({ isActive }: { isActive: boolean }): string =>
  `block rounded-xl px-3 py-2 text-left transition ${
    isActive ? 'bg-white text-cocoa font-semibold shadow-sm' : 'text-cocoa/80 hover:bg-white/70'
  }`;

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, role, profile, signOut, initializing } = useAuth();
  const sponsorFeature = sponsorsEnabled();
  const [mobileOpen, setMobileOpen] = useState(false);
  const bearer = useBearer();
  const [unseen, setUnseen] = useState(0);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navItems = useMemo(() => {
    const base = [
      { to: '/', label: 'Home', end: true },
      { to: '/programs', label: 'Programs' },
      { to: '/prospects', label: 'Prospects' },
      { to: '/gallery', label: 'Gallery' },
      { to: '/events', label: 'Events' },
      { to: '/community', label: 'Community' },
      { to: '/partners', label: 'Partners' },
      { to: '/contact', label: 'Contact' },
      { to: '/nominate', label: 'Nominate' },
      { to: '/portal/nominee', label: 'The Pool' },
    ];

    if (sponsorFeature) {
      base.push({
        to: role === 'sponsor' ? '/portal/sponsor' : '/sponsor',
        label: role === 'sponsor' ? 'Sponsor Portal' : 'Sponsor',
      });
      if (role === 'sponsor') {
        base.push({ to: '/portal/sponsor/credits', label: 'Credits' });
      }
    }

    return base;
  }, [role, sponsorFeature]);

  const signedIn = Boolean(session?.access_token);
  const adminRole = profile?.role || role;
  const isAdmin = ['admin', 'superadmin'].includes(adminRole ?? '');
  const portalPath = profile?.role ? roleToPath(profile.role) : role ? roleToPath(role) : '/login';

  useEffect(() => {
    if (!signedIn || !isAdmin || !bearer) {
      setUnseen(0);
      return;
    }
    if (typeof window === 'undefined') return;

    let cancelled = false;
    (async () => {
      try {
        const u = new URL('/api/v1/admin/notifications', window.location.origin);
        u.searchParams.set('unread', 'true');
        const res = await fetch(u, { headers: { ...bearer } });
        const json = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) {
          const items = Array.isArray(json?.data) ? json.data : [];
          setUnseen(items.length);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('admin notifications badge error', error);
          setUnseen(0);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bearer, isAdmin, signedIn]);

  const handleNavClick = () => setMobileOpen(false);
  const handleBrandClick = () => {
    handleNavClick();
    navigate(signedIn ? portalPath : '/');
  };
  const handleSignOut = () => {
    setMobileOpen(false);
    signOut();
  };

  return (
    <header className="relative z-30 w-full border-b border-black/5 bg-sand/90 px-4 py-4 backdrop-blur supports-[backdrop-filter]:bg-sand/70">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4">
        <button
          type="button"
          onClick={handleBrandClick}
          className={`flex items-center gap-3 ${
            signedIn
              ? 'rounded-md ring-2 ring-amber-400/70 transition-shadow hover:ring-amber-500/90'
              : 'transition'
          }`}
          aria-label="Project Tower"
          title={signedIn ? 'Enter portal' : 'Home'}
        >
          <img src="/logo.png" alt="Project Tower logo" className="h-10 w-auto" />
          <span className="font-bold text-xl tracking-wide text-cocoa">Project Tower</span>
        </button>

        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-sand text-cocoa shadow-soft transition hover:bg-white md:hidden"
          onClick={() => setMobileOpen((open) => !open)}
          aria-expanded={mobileOpen ? 'true' : 'false'}
          aria-controls="primary-navigation"
        >
          <span className="sr-only">Toggle navigation</span>
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            {mobileOpen ? <path d="M6 6l12 12M18 6l-12 12" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
          </svg>
        </button>

        <nav className="hidden items-center gap-2 text-sm md:flex">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={navLinkClass} onClick={handleNavClick}>
              {item.label}
            </NavLink>
          ))}

          {signedIn && isAdmin ? (
            <a
              href="/admin/notifications"
              className="relative inline-flex h-10 items-center justify-center rounded-full border border-sand px-3 text-cocoa shadow-soft transition hover:bg-white"
              title="Admin notifications"
            >
              <span className="sr-only">Admin notifications</span>
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 22a2 2 0 0 0 2-2H10a2 2 0 0 0 2 2z" />
                <path d="M18 16v-3a6 6 0 1 0-12 0v3l-1.5 1.5a1 1 0 0 0 .7 1.7h13.6a1 1 0 0 0 .7-1.7L18 16z" />
              </svg>
              {unseen > 0 ? (
                <span className="absolute -top-1 -right-1 rounded-full bg-amber-600 px-1 text-[10px] font-semibold text-white">
                  {unseen}
                </span>
              ) : null}
            </a>
          ) : null}

          {session ? (
            <button
              type="button"
              className="badge shadow-soft bg-brick text-white hover:bg-brickDark transition"
              onClick={handleSignOut}
              disabled={initializing}
            >
              Sign out {role ? `(${role})` : ''}
            </button>
          ) : (
            <NavLink to="/login" className={navLinkClass} onClick={handleNavClick}>
              Login
            </NavLink>
          )}
        </nav>
      </div>

      {mobileOpen ? (
        <div
          id="primary-navigation"
          className="mx-auto mt-4 w-full max-w-6xl rounded-2xl bg-white/90 p-4 text-sm shadow-soft backdrop-blur supports-[backdrop-filter]:bg-white/70 md:hidden"
        >
          <div className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={`mobile-${item.to}`}
                to={item.to}
                end={item.end}
                className={mobileNavLinkClass}
                onClick={handleNavClick}
              >
                {item.label}
              </NavLink>
            ))}
            {signedIn && isAdmin ? (
              <NavLink
                to="/admin/notifications"
                className={mobileNavLinkClass}
                onClick={handleNavClick}
              >
                Admin notifications{unseen > 0 ? ` (${unseen})` : ''}
              </NavLink>
            ) : null}
          </div>

          <div className="mt-4 border-t border-sand pt-4">
            {session ? (
              <button
                type="button"
                className="w-full rounded-xl bg-brick px-3 py-2 font-semibold text-white shadow-soft transition hover:bg-brickDark disabled:opacity-70"
                onClick={handleSignOut}
                disabled={initializing}
              >
                Sign out {role ? `(${role})` : ''}
              </button>
            ) : (
              <NavLink
                to="/login"
                className="block rounded-xl px-3 py-2 text-center font-semibold text-cocoa transition hover:bg-shell"
                onClick={handleNavClick}
              >
                Login
              </NavLink>
            )}
          </div>
        </div>
      ) : null}
    </header>
  );
}
